import NewsInlineBanner from '@/components/NewsInlineBanner'
import WatermarkedImage from '@/components/WatermarkedImage'
import { interleaveNewsBanners } from '@/lib/newsBanners'
import { formatNewsDate } from '@/lib/news'
import type { NewsArticle } from '@/types/news'
import { NEWS_HOME_PAGE_SIZE } from '@/types/news'
import type { NewsBanner } from '@/types/radio'
import {ChevronRight, Newspaper} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

interface NewsPreviewProps {
  articles: NewsArticle[]
  loading?: boolean
  newsBanners?: NewsBanner[]
}

function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl bg-white/10 sm:rounded-2xl">
      <div className="aspect-[4/3] animate-pulse bg-white/15 sm:aspect-[16/10]" />
      <div className="space-y-1.5 p-2 sm:p-4">
        <div className="h-3 w-14 animate-pulse rounded-full bg-white/20" />
        <div className="h-4 w-11/12 animate-pulse rounded bg-white/20" />
        <div className="h-3 w-full animate-pulse rounded bg-white/10" />
      </div>
    </div>
  )
}

function NewsPreview({ articles, loading = false, newsBanners = [] }: NewsPreviewProps) {
  const published = useMemo(
    () => (articles || []).filter((item) => item?.published !== false),
    [articles],
  )
  const [page, setPage] = useState(0)
  const totalPages = Math.max(1, Math.ceil(published.length / NEWS_HOME_PAGE_SIZE))
  const currentPage = Math.min(page, totalPages - 1)
  const slice = published.slice(
    currentPage * NEWS_HOME_PAGE_SIZE,
    currentPage * NEWS_HOME_PAGE_SIZE + NEWS_HOME_PAGE_SIZE,
  )
  const pageNumbers = useMemo(() => Array.from({ length: totalPages }, (_, index) => index), [totalPages])
  const feed = useMemo(
    () => interleaveNewsBanners(slice.map((item) => item._id || item.slug), newsBanners, 2),
    [slice, newsBanners],
  )
  const articlesByKey = useMemo(() => {
    const map = new Map<string, NewsArticle>()
    slice.forEach((item) => map.set(item._id || item.slug, item))
    return map
  }, [slice])

  if (!loading && !published.length) return null

  return (
    <section id="noticias" className="mx-auto mt-12 max-w-7xl px-3 sm:mt-16 sm:px-4 lg:mt-20 lg:px-6">
      <div className="mb-6 flex items-end justify-between gap-4 sm:mb-8">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#ffd21f] text-[#071a63]">
              <Newspaper size={20} />
            </span>
            <h2 className="font-display text-2xl font-black tracking-wide sm:text-3xl">NOTÍCIAS</h2>
          </div>
          <p className="mt-2 text-[11px] font-semibold tracking-[0.16em] text-white/70 sm:text-xs">
            AS ÚLTIMAS MATÉRIAS DA RÁDIO
          </p>
        </div>
        <Link
          to="/noticias"
          className="shrink-0 rounded-full border border-white/20 px-3 py-2 text-[11px] font-bold sm:px-4 sm:text-xs"
        >
          VER TODAS {'>'}
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 xl:grid-cols-4 xl:gap-6">
          {Array.from({ length: NEWS_HOME_PAGE_SIZE }).map((_, index) => (
            <CardSkeleton key={index} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 xl:grid-cols-4 xl:gap-6">
          {feed.map((entry) => {
            if (entry.kind === 'banner') {
              return (
                <div key={entry.key} className="col-span-full">
                  <NewsInlineBanner banner={entry.banner} />
                </div>
              )
            }
            const item = articlesByKey.get(entry.key)
            if (!item) return null
            return (
              <Link
                key={item._id || item.slug}
                to={`/noticias/${item.slug}`}
                className="group flex flex-col overflow-hidden rounded-xl bg-white text-[#12307a] shadow-md transition hover:-translate-y-0.5 hover:shadow-xl sm:rounded-2xl"
              >
                <WatermarkedImage
                  src={item.featuredImage}
                  alt={item.title}
                  className="aspect-[4/3] bg-[#071a63] sm:aspect-[16/10]"
                  imgClassName="h-full w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-2.5 sm:p-4">
                  <span className="w-fit rounded-full bg-[#edf3ff] px-1.5 py-0.5 text-[8px] font-extrabold uppercase tracking-wide text-[#2a63e8] sm:px-2 sm:text-[10px]">
                    {item.category}
                  </span>
                  <h3 className="mt-1.5 line-clamp-2 font-display text-[12px] font-black leading-snug sm:text-[15px]">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 flex-1 text-[11px] leading-relaxed text-slate-600 sm:line-clamp-2 sm:text-xs">
                    {item.excerpt}
                  </p>
                  <p className="mt-1.5 truncate text-[9px] font-bold uppercase tracking-wide text-[#2a63e8]/80 sm:text-[10px]">
                    Por {item.author || 'Redação'}
                  </p>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="truncate text-[9px] font-semibold uppercase text-slate-400 sm:text-[10px]">{formatNewsDate(item.createdAt)}</span>
                    <span className="shrink-0 rounded-full bg-[#0b2ea8] px-2 py-1 text-[9px] font-extrabold uppercase tracking-wide text-white transition group-hover:bg-[#1646d6] sm:px-3 sm:py-1.5 sm:text-[11px]">
                      Leia +
                    </span>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {!loading && published.length > NEWS_HOME_PAGE_SIZE ? (
        <nav className="mt-7 flex flex-wrap items-center justify-center gap-2 sm:mt-9" aria-label="Paginação das notícias">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(0, value - 1))}
            disabled={currentPage === 0}
            className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#0b2ea8] disabled:opacity-40"
            aria-label="Matérias anteriores"
          >
            <ChevronRight className="rotate-180" size={18} />
          </button>
          {pageNumbers.map((index) => {
            const active = index === currentPage
            return (
              <button
                key={index}
                type="button"
                onClick={() => setPage(index)}
                aria-current={active ? 'page' : undefined}
                className={`grid h-10 min-w-10 place-items-center rounded-full px-3 text-sm font-extrabold ${
                  active ? 'bg-[#ffd21f] text-[#071a63]' : 'bg-white/15 text-white hover:bg-white/25'
                }`}
              >
                {index + 1}
              </button>
            )
          })}
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}
            disabled={currentPage >= totalPages - 1}
            className="grid h-10 w-10 place-items-center rounded-full bg-white text-[#0b2ea8] disabled:opacity-40"
            aria-label="Ver mais notícias"
          >
            <ChevronRight size={18} />
          </button>
        </nav>
      ) : null}
    </section>
  )
}

export default NewsPreview
